import type { PayloadAction } from '@reduxjs/toolkit';
import { createAction, createSlice } from '@reduxjs/toolkit';

import { MAX_SELECTED_SYMBOLS } from '@/constants/constants';
import type { SymbolKey } from '@/entities/symbol';
import { symbolKey } from '@/entities/symbol';

export enum SELECTOR_MODES {
  NEUTRAL,
  SPOT,
  FORWARD_CURVE
}

type SymbolSelectorState = {
  symbols: SymbolKey[];
  previousSymbols: SymbolKey[] | null;
  limitReached: boolean;
  isDashboardAppSelector: boolean; // true when used in Dashboard app context
  selectorMode: SELECTOR_MODES;
};

const initialState: SymbolSelectorState = {
  symbols: [],
  previousSymbols: null,
  limitReached: false,
  isDashboardAppSelector: false,
  selectorMode: SELECTOR_MODES.NEUTRAL
};

type SymbolAction = {
  symbol: SymbolKey;
};

type SymbolArrayAction = {
  symbols: SymbolKey[];
};

const buildKeySet = (symbols: SymbolKey[], isForwardMode: boolean) =>
  new Set(symbols.map(s => symbolKey(s, isForwardMode)));

const filterByKey = (symbols: SymbolKey[], excludeKeys: Set<string>, isForwardMode: boolean) =>
  symbols.filter(s => !excludeKeys.has(symbolKey(s, isForwardMode)));

const updateLimitReached = (state: SymbolSelectorState) => {
  state.limitReached = state.symbols.length >= MAX_SELECTED_SYMBOLS;
};

const buildIndexByKey = (symbols: SymbolKey[], isForwardMode: boolean) => {
  const indexByKey = new Map<string, number>();
  for (let i = 0; i < symbols.length; i++) {
    indexByKey.set(symbolKey(symbols[i], isForwardMode), i);
  }
  return indexByKey;
};

const computeToggleChanges = (
  currentSymbols: SymbolKey[],
  incomingSymbols: SymbolKey[],
  isForwardMode: boolean
) => {
  const existingKeys = buildKeySet(currentSymbols, isForwardMode);
  const indexByKey = buildIndexByKey(currentSymbols, isForwardMode);
  const toRemove = new Set<number>();
  const toAdd: SymbolKey[] = [];
  let limitReached = false;

  for (const sym of incomingSymbols) {
    const k = symbolKey(sym, isForwardMode);
    if (existingKeys.has(k)) {
      const idx = indexByKey.get(k);
      if (idx != null) toRemove.add(idx);
      continue;
    }

    if (currentSymbols.length - toRemove.size + toAdd.length >= MAX_SELECTED_SYMBOLS) {
      limitReached = true;
      continue;
    }

    toAdd.push(sym);
  }

  return { toRemove, toAdd, limitReached };
};

const applyToggleChanges = (
  currentSymbols: SymbolKey[],
  toRemove: Set<number>,
  toAdd: SymbolKey[]
) => {
  const next: SymbolKey[] = [];
  for (let i = 0; i < currentSymbols.length; i++) {
    if (!toRemove.has(i)) next.push(currentSymbols[i]);
  }
  return [...toAdd, ...next];
};

const isForwardMode = (state: SymbolSelectorState) =>
  state.selectorMode === SELECTOR_MODES.FORWARD_CURVE;

const symbolSelectorSlice = createSlice({
  name: 'symbolSelector',
  initialState,
  reducers: {
    setSymbols: (state, action: PayloadAction<SymbolKey[]>) => {
      state.symbols = action.payload;
    },
    addSymbol: (state, { payload }: PayloadAction<SymbolAction>) => {
      const { symbol } = payload;
      const keys = buildKeySet(state.symbols, isForwardMode(state));
      if (keys.has(symbolKey(symbol, isForwardMode(state)))) return;

      if (state.symbols.length >= MAX_SELECTED_SYMBOLS) {
        state.limitReached = true;
        return;
      }

      state.symbols = [symbol, ...state.symbols];
    },

    addSymbols: (state, { payload }: PayloadAction<SymbolArrayAction>) => {
      const keys = buildKeySet(state.symbols, isForwardMode(state));
      const toAdd: SymbolKey[] = [];
      for (const s of payload.symbols) {
        const k = symbolKey(s, isForwardMode(state));
        if (keys.has(k)) continue;
        if (state.symbols.length + toAdd.length >= MAX_SELECTED_SYMBOLS) {
          state.limitReached = true;
          break;
        }
        keys.add(k);
        toAdd.push(s);
      }
      if (toAdd.length) {
        state.symbols = [...state.symbols, ...toAdd];
      }
    },

    deleteSymbol: (state, { payload }: PayloadAction<SymbolAction>) => {
      const k = symbolKey(payload.symbol, isForwardMode(state));
      state.symbols = state.symbols.filter(s => symbolKey(s, isForwardMode(state)) !== k);
      updateLimitReached(state);
    },

    deleteSymbols: (state, { payload }: PayloadAction<{ symbols: SymbolKey[] }>) => {
      const deleteSet = buildKeySet(payload.symbols, isForwardMode(state));
      state.symbols = filterByKey(state.symbols, deleteSet, isForwardMode(state));
    },

    reorderSymbols: (state, { payload }: PayloadAction<SymbolArrayAction>) => {
      state.symbols = payload.symbols;
    },

    toggleSymbol: (state, { payload }: PayloadAction<SymbolAction>) => {
      const { symbol } = payload;
      const k = symbolKey(symbol, isForwardMode(state));
      const keys = buildKeySet(state.symbols, isForwardMode(state));

      if (keys.has(k)) {
        state.symbols = state.symbols.filter(s => symbolKey(s, isForwardMode(state)) !== k);
        updateLimitReached(state);
      } else if (state.symbols.length >= MAX_SELECTED_SYMBOLS) {
        state.limitReached = true;
      } else {
        state.symbols = [symbol, ...state.symbols];
      }
    },

    toggleSymbolsArray: (state, { payload }: PayloadAction<SymbolArrayAction>) => {
      const { toRemove, toAdd, limitReached } = computeToggleChanges(
        state.symbols,
        payload.symbols,
        isForwardMode(state)
      );

      if (limitReached) {
        state.limitReached = true;
      }

      if (toRemove.size || toAdd.length) {
        state.symbols = applyToggleChanges(state.symbols, toRemove, toAdd);
        updateLimitReached(state);
      }
    },

    overrideSymbols: (state, { payload }: PayloadAction<SymbolArrayAction>) => {
      state.previousSymbols = state.symbols;
      state.symbols = payload.symbols;
      updateLimitReached(state);
    },

    restoreSymbols: state => {
      if (state.previousSymbols !== null) {
        state.symbols = state.previousSymbols;
        state.previousSymbols = null;
        updateLimitReached(state);
      }
    },

    clearSelection: state => {
      state.symbols = [];
      state.limitReached = false;
      state.selectorMode = SELECTOR_MODES.NEUTRAL;
    },

    dismissLimit: state => {
      state.limitReached = false;
    },

    setIsDashboardAppSelector: (state, action: PayloadAction<boolean>) => {
      state.isDashboardAppSelector = action.payload;
    },

    setSelectorMode: (state, action: PayloadAction<SELECTOR_MODES>) => {
      if (action.payload === SELECTOR_MODES.NEUTRAL) {
        state.symbols = [];
        state.selectorMode = SELECTOR_MODES.NEUTRAL;
      } else if (
        state.selectorMode !== SELECTOR_MODES.NEUTRAL &&
        action.payload !== state.selectorMode
      ) {
        // Incompatible mode change — UI layer already showed the snackbar warning
        return;
      } else {
        state.selectorMode = action.payload;
      }
    }
  }
});

export const {
  setSymbols,
  addSymbol,
  addSymbols,
  deleteSymbol,
  clearSelection,
  dismissLimit,
  deleteSymbols,
  reorderSymbols,
  toggleSymbol,
  toggleSymbolsArray,
  setIsDashboardAppSelector,
  overrideSymbols,
  restoreSymbols,
  setSelectorMode
} = symbolSelectorSlice.actions;

// Intent actions: components dispatch these; selectionListener handles mode conflict,
// snackbar, and the actual symbol + mode dispatches.
export const requestSpotSelection = createAction<{ symbol: SymbolKey } | { symbols: SymbolKey[] }>(
  'symbolSelector/requestSpotSelection'
);

export const requestForwardCurveSelection = createAction<{ symbols: SymbolKey[] }>(
  'symbolSelector/requestForwardCurveSelection'
);

export default symbolSelectorSlice.reducer;
